import React, { forwardRef, useRef, useState } from 'react';
import {
    Col,
    FormGroup,
    InputGroupAddon,
    ModalBody,
    ModalHeader,
    Row,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import './GetReceiptByDateRange.scss';
import Rodal from '../Rodal/Rodal';
import { useStateSelector } from '../../store/selectors';
import createNotification from '../../utils/createNotification';
import ButtonLoader from '../Layout/Buttons/ButtonLoader';
import { useCurrentMarketplaceParty } from '../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';

interface GetReceiptByDateRangeProps {
    isAdmin?: boolean;
    userIds?: number[];
}

export const GetReceiptByDateRange = (props: GetReceiptByDateRangeProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(
        moment().add(1, 'days').toDate()
    );
    const startRef = useRef(null);
    const endRef = useRef(null);
    const DateControlInput = forwardRef(({ value, onClick }: any, ref: any) => (
        <div ref={ref} className={'btn'} onClick={onClick}>
            {value}
        </div>
    ));
    const axios = useStateSelector((state) => state.core.axios);
    const party = useCurrentMarketplaceParty();
    const isBroker = party === TimelineParty.Broker;
    const [isLoading, setIsLoading] = useState(false);

    const fetchReceipt = () => {
        const requestUrl = props.isAdmin
            ? '/api/Receipts/dateRange/marketplace/admin'
            : '/api/Receipts/dateRange/marketplace';

        setIsLoading(true);
        axios
            .post(
                requestUrl,
                {
                    startDate: startDate,
                    endDate: endDate,
                    userIds: props.userIds,
                },
                {
                    responseType: 'blob',
                }
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification('Error downloading receipts', 'error');
                    return;
                }

                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: 'application/zip' })
                );

                const startStamp = moment(startDate).format('L');
                const endStamp = moment(endDate).format('L');

                const fileName = `Receipts_${startStamp}_${endStamp}.zip`;

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(url);
            })
            .catch(() => {
                createNotification('Error fetching receipts', 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div className="get-receipt-by-date-range">
            {isBroker ? (
                <></>
            ) : (
                <button
                    className="btn btn-primary"
                    onClick={() => setIsVisible(true)}>
                    Print Receipts
                </button>
            )}

            <Rodal
                visible={isVisible}
                onClose={() => setIsVisible(false)}
                animation="slideUp"
                className="print-date-range-popup"
                showMask={true}
                width={420}
                showCloseButton={true}>
                <ModalHeader>Select Dates</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md={4} className="p-0">
                            <label>Start Date:</label>
                        </Col>
                        <Col md={8} className="p-0">
                            <FormGroup className="d-flex small-border">
                                <InputGroupAddon
                                    addonType="prepend"
                                    onClick={() => {
                                        startRef.current?.setOpen(true);
                                    }}>
                                    <div className="input-group-text">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                    </div>
                                </InputGroupAddon>
                                <DatePicker
                                    portalId="start-date-portal"
                                    ref={startRef}
                                    className="form-control"
                                    selected={startDate}
                                    name="startDate"
                                    popperProps={{ strategy: 'fixed' }}
                                    onChange={(value: Date) => {
                                        if (endDate && value > endDate) {
                                            const oldEnd = endDate;
                                            setEndDate(value);
                                            setStartDate(oldEnd);
                                        } else {
                                            setStartDate(value);
                                        }
                                    }}
                                    dateFormat="MM/dd/yyyy"
                                    customInput={<DateControlInput />}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4} className="p-0">
                            <label>End Date:</label>
                        </Col>
                        <Col md={8} className="p-0">
                            <FormGroup className="d-flex small-border">
                                <InputGroupAddon
                                    addonType="prepend"
                                    onClick={() => {
                                        endRef.current?.setOpen(true);
                                    }}>
                                    <div className="input-group-text">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                    </div>
                                </InputGroupAddon>
                                <DatePicker
                                    portalId="end-date-portal"
                                    minDate={startDate}
                                    ref={endRef}
                                    className="form-control"
                                    selected={endDate}
                                    name="endDate"
                                    popperProps={{ strategy: 'fixed' }}
                                    onChange={(value: Date) => {
                                        if (startDate && value < startDate) {
                                            const oldStart = startDate;
                                            setStartDate(value);
                                            setEndDate(oldStart);
                                        } else {
                                            setEndDate(value);
                                        }
                                    }}
                                    dateFormat="MM/dd/yyyy"
                                    customInput={<DateControlInput />}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <div className="buttons-wrapper">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => setIsVisible(false)}>
                            Cancel
                        </button>
                        <ButtonLoader
                            type="submit"
                            className={'btn btn-primary'}
                            disabled={isLoading}
                            isLoading={isLoading}
                            loaderButtonText={''}
                            buttonText={'Print'}
                            onClick={fetchReceipt}
                        />
                    </div>
                </ModalBody>
            </Rodal>
        </div>
    );
};
